import { Card, CardActions, CardContent, Grid, Typography, useTheme } from '@mui/material';
import { CerebroLink, CerebroText } from '../../../shared/components';
import { CurrentUser, TermSheet, TermSheetRevision } from '../../../shared/models';
import { DateUtils, EnumUtils, FileUtils, NumberUtils } from '../../../shared/utils';
import { TermSheetActionButton } from './TermSheetActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FormatCurrency } from '../../../shared/utils/number';
import { FormatStringAsShortDate } from '../../../shared/utils/date';
import { CerebroApiEndpoints } from '../../../shared/utils/api';

interface TermSheetListItemProps {
	dataRoomGuid: string | undefined;
	sheet: TermSheet;
	currentRevision: TermSheetRevision | null;
	currentUser: CurrentUser;
	enterFeedback: (revision: TermSheetRevision | null) => void;
	editRevision: (revision: TermSheetRevision | null) => void;
	newRevision: (revision: TermSheetRevision | null) => void;
	deleteTermSheet: (sheet: TermSheet | null) => void;
	revisionList: (sheet: TermSheet | null) => void;
}

export const TermSheetListItem: React.FC<TermSheetListItemProps> = ({
	dataRoomGuid,
	sheet,
	currentRevision,
	currentUser,
	enterFeedback,
	editRevision,
	newRevision,
	deleteTermSheet,
	revisionList,
}) => {
	const isEmployee = currentUser.isEmployeeUser;
	const theme = useTheme();

	return (
		<Card
			key={sheet.guid}
			elevation={3}
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				flexDirection: 'column',
				height: '100%',
				marginBottom: 2,
			}}
		>
			<CardContent sx={{ paddingBottom: '0 !important' }}>
				<Grid container sx={{ marginBottom: 2 }}>
					<Grid item xs={8} sx={{ display: 'flex' }}>
						<CerebroText variant="large" component="span" sx={{ fontWeight: 'bold' }}>
							{sheet.bankName}
						</CerebroText>
					</Grid>
					<Grid item xs={4} sx={{ textAlign: 'right' }}>
						{sheet.lenderName}
					</Grid>
				</Grid>
				{sheet.revisions
					.sort((a, b) => b.versionNumber - a.versionNumber)
					.map(revision => {
						let totalAmount: number = 0;
						revision.facilities.map(facility => {
							totalAmount += facility.highEndAmount ?? 0;
						});

						if (currentUser.isCompanyUser && !revision.visibleToBorrower) return <></>;

						return (
							<CerebroText key={revision.guid}>
								Version {revision.versionNumber}
								{', '}
								{DateUtils.FormatStringAsShortDate(revision.dateReceived?.toString())}
								{': '}
								{NumberUtils.FormatCurrency(totalAmount)}{' '}
								{isEmployee && (
									<CerebroLink
										variant="link"
										sx={{ marginLeft: 2 }}
										onClick={() => editRevision(revision)}
									>
										Edit
									</CerebroLink>
								)}
								<CerebroLink
									href={CerebroApiEndpoints.TermSheets.AbsoluteDownload(
										dataRoomGuid,
										sheet.guid,
										revision.guid
									)}
									sx={{ marginLeft: 2 }}
									target="_blank"
									rel="noreferrer"
									variant="link"
								>
									Download
								</CerebroLink>
								{isEmployee && revision.incompleteData && revision.incompleteData.length > 0 && (
									<CerebroText
										component="span"
										sx={{ marginLeft: 2, color: theme.palette.warning.main }}
									>
										Missing Information
									</CerebroText>
								)}
							</CerebroText>
						);
					})}
			</CardContent>
			<CardActions
				sx={theme => ({
					alignItems: 'flex-end',
					backgroundColor: theme.palette.gray.background,
					borderTop: `1px solid ${theme.palette.divider}`,
					justifyContent: 'right',
				})}
			>
				<Grid container>
					<Grid item xs={6}>
						<CerebroText sx={{ paddingTop: 2 }}>
							{EnumUtils.GetTermSheetResponse(sheet.responseStatus)}, Submitted on{' '}
							{FormatStringAsShortDate(sheet.submittedOn?.toString())}
						</CerebroText>
					</Grid>
					<Grid item xs={6} sx={{ textAlign: 'right' }}>
						<TermSheetActionButton
							dataRoomGuid={dataRoomGuid}
							currentUser={currentUser}
							sheet={sheet}
							currentRevision={currentRevision}
							enterFeedback={enterFeedback}
							editRevision={editRevision}
							newRevision={newRevision}
							deleteTermSheet={deleteTermSheet}
							revisionList={revisionList}
						/>
					</Grid>
				</Grid>
			</CardActions>
		</Card>
	);
};
